import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import * as projection from "@arcgis/core/geometry/projection";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import GroupLayer from "@arcgis/core/layers/GroupLayer";

const SYMBOLS = {
  "polygon-3d": {
    type: "polygon-3d", // autocasts as new PolygonSymbol3D()
    castShadows: true,
    symbolLayers: [
      {
        type: "fill", // autocasts as new ExtrudeSymbol3DLayer()
        material: {
          // color: [244, 247, 134, 0.5],
          color: [51, 51, 204, 0.1],
        },
        edges: {
          type: "solid", // autocasts as new SolidEdges3D()
          color: [50, 50, 50, 0.6],
        },
      },
    ],
  },

  "polygon-3d-slope": {
    type: "polygon-3d", // autocasts as new PolygonSymbol3D()
    castShadows: true,
    symbolLayers: [
      {
        type: "fill", // autocasts as new ExtrudeSymbol3DLayer()
        outline: {
          color: [0, 255, 255, 0.75],
          size: "8px",
          pattern: {
            // autocasts as new LineStylePattern3D()
            type: "style",
            style: "solid",
          },
          patternCap: "round",
        },
      },
    ],
  },
  polygon: {
    type: "simple-fill", // autocasts as new SimpleFillSymbol()
    color: [51, 51, 204, 0.1],
    style: "solid",
    outline: {
      // autocasts as new SimpleLineSymbol()
      color: [0, 0, 255, 1],
      width: 1,
    },
  },
  "polygon-slope": {
    type: "simple-fill", // autocasts as new SimpleFillSymbol()
    color: [0, 0, 0, 0],
    outline: {
      color: [0, 255, 255, 0.75],
      width: 4,
    },
  },
  point: {
    type: "simple-marker",
    // color: [255, 121, 121, 0.25],
    color: [51, 51, 204, 0.5],
  },
  pointOfElevProfile: {
    type: "simple-marker",
    size: "3px",
    color: [0, 0, 0],
    outline: {
      color: [255, 255, 255, 0],
      width: 0,
    },
  },
  point3dOfElevProfile: {
    type: "point-3d",
    symbolLayers: [
      {
        type: "icon",
        size: 3,
        resource: {
          primitive: "circle",
        },
        material: {
          color: "black",
        },
      },
    ],
  },
  "polyline-3d": {
    type: "line-3d",
    symbolLayers: [
      {
        type: "line",
        size: 5,
        material: { color: [51, 51, 204, 0.7] },
      },
    ],
  },

  polyline: { type: "simple-line", width: 5, color: [51, 51, 204, 0.7] },
};
class geometryTools {
  static Graphic = Graphic;
  static SpatialReference = SpatialReference;
  static Point = Point;
  static Polygon = Polygon;
  static Polyline = Polyline;
  static geometryEngine = geometryEngine;
  static FeatureLayer = FeatureLayer;
  static MapImageLayer = MapImageLayer;
  static GroupLayer = GroupLayer;
  static highlightSymbol = SYMBOLS;
  static async buffer(
    geometry,
    distance = 0,
    unit = "meters",
    unionResults = false
  ) {
    if (!geometry || distance == 0) {
      return geometry;
    }
    var oldSp = null;
    if (!geometry.spatialReference.isWGS84) {
      oldSp = geometry.spatialReference;
      await projection.load();
      geometry = projection.project(geometry, {
        wkid: 3857,
      });
    }
    var buffered = geometryEngine.buffer(
      geometry,
      distance,
      unit,
      unionResults
    );
    if (oldSp) {
      buffered = projection.project(buffered, oldSp);
    }
    return buffered;
  }
  static union(geometrys) {
    let unionedGeometry = geometryEngine.union(geometrys);
    return unionedGeometry;
  }
  static zoomToGraphic(mapObj, geometrys) {
    this.graphicFuncMain(mapObj, "", geometrys, true, true);
  }
  static refreshGraphicByLayerId(
    mapObj,
    layerId,
    geometrys,
    zoomToFeature,
    isSlope,
    isAnimated
  ) {
    this.graphicFuncMain(
      mapObj,
      layerId,
      geometrys,
      zoomToFeature,
      undefined,
      isSlope,
      isAnimated
    );
  }
  static refreshGraphicInMeasureLayerById(
    mapObj,
    layerId,
    geometrys,
    extrudeHeight
  ) {
    let measureLayer = null;
    measureLayer = mapObj.map.findLayerById(layerId);
    if (measureLayer) {
      measureLayer.removeAll();
    } else {
      this.createGraphicsLayerById(layerId, mapObj.map);
      measureLayer = mapObj.map.findLayerById(layerId);
    }
    if (!geometrys || !Array.isArray(geometrys)) {
      return;
    } else {
      let tmpSymbol = SYMBOLS["polygon-3d"];
      tmpSymbol.symbolLayers[0].size = extrudeHeight;
      for (let i = 0; i < geometrys.length; i++) {
        let tmpGraphic = new Graphic({
          geometry: geometrys[i],
          symbol: tmpSymbol,
        });
        measureLayer.add(tmpGraphic);
      }
    }
  }
  static graphicFuncMain(
    mapObj,
    layerId,
    geometrys,
    zoomToFeature,
    zoomToOnly = false,
    isSlope = false,
    animate = true
  ) {
    if (!geometrys || geometrys.length === 0) {
      return;
    }
    let highlightLayer = null;
    if (!zoomToOnly) {
      highlightLayer = mapObj.map.findLayerById(layerId);
      highlightLayer?.removeAll();
    }
    if (!Array.isArray(geometrys)) geometrys = [geometrys];
    geometrys = geometrys.filter((i) => !!i);
    if (geometrys.length === 0) return;
    let geometry = null;
    if (!zoomToOnly) {
      let is3D = mapObj.type === "3d";
      for (let i = 0; i < geometrys.length; i++) {
        let symbolName = geometrys[i]?.type;
        if (typeof symbolName === "undefined") return;
        if (symbolName != "point" && is3D) symbolName += "-3d";
        symbolName += isSlope ? "-slope" : "";
        let tmpGraphic = new Graphic({
          geometry: geometrys[i],
          symbol: SYMBOLS[symbolName],
        });
        highlightLayer?.add(tmpGraphic);
      }
    }
    geometry =
      geometrys.length > 1
        ? geometryEngine.convexHull(geometrys)
        : geometrys[0];

    if (zoomToFeature) {
      if (geometry.type === "point") {
        let geoZoom = {
          target: geometry,
          zoom: 18,
        };
        mapObj.goTo(geoZoom);
      } else {
        mapObj.goTo(geometry, { animate });
      }
    }
  }
  static createGraphicMain(attr, map) {
    let tmpLayer = null;
    if (attr.id) {
      tmpLayer = map.findLayerById(attr.id);
    }
    if (!tmpLayer) {
      tmpLayer = new GraphicsLayer(attr);
      map.add(tmpLayer);
    }

    let tempElevationInfo = {
      mode: "on-the-ground",
      // mode: "relative-to-ground",
      // offset: 1,
      // featureExpressionInfo: {
      //   expression: "Geometry($feature).z"
      // },
      // unit: "meters"
    };
    tmpLayer.elevationInfo = tempElevationInfo;
    return tmpLayer;
  }
  static createGraphicsLayerById(layerId, map) {
    return this.createGraphicMain({ id: layerId }, map);
  }
  static createGraphicsLayerByElev(title, map, mode = "on-the-ground") {
    let tmpLayer = this.createGraphicMain(
      {
        elevationInfo: { mode: mode },
        title: title,
      },
      map
    );
    return tmpLayer;
  }
  static clearGraphicsByLayerId(layerId, map) {
    let tmpLayer = map.findLayerById(layerId);
    tmpLayer && tmpLayer.removeAll();
  }
  static flashSymbol(
    layer,
    graphic,
    func,
    t = 0,
    lastTimestamp = Date.now(),
    restoreSymbol
  ) {
    if (t === 0) restoreSymbol = graphic.symbol;
    layer.remove(graphic);
    graphic = graphic.clone();
    func(graphic, Math.sin((t / 1000) * Math.PI));
    layer.add(graphic);
    if (t < 1000) {
      let now = Date.now();
      let delta = now - lastTimestamp;
      t += delta;
      setTimeout(
        () => geometryTools.flashSymbol(layer, graphic, func, t, now),
        33
      );
    } else layer.remove(graphic);
  }
  static async analysisLineSegment(geometry, GroundSegment) {
    const length = geometryEngine.planarLength(geometry, "meters");

    // Assuming you have a polyline geometry named 'polyline' and you want to find partial lengths for each segment

    // Get the number of paths in the polyline
    var pathCount = geometry.paths.length;
    var maxSegmentLength = 0;
    GroundSegment = [];

    // Iterate through each path
    for (var i = 0; i < pathCount; i++) {
      var path = geometry.paths[i];
      var pointCount = path.length;

      // Iterate through each pair of consecutive points in the path
      for (var j = 0; j < pointCount - 1; j++) {
        var startPoint = path[j];
        var endPoint = path[j + 1];

        // Create a new polyline segment using the consecutive points
        var segment = {
          paths: [[startPoint, endPoint]],
          spatialReference: geometry.spatialReference,
        };

        // Calculate the length of the segment using geometryEngine.planarLength
        var segmentLength = geometryEngine.planarLength(segment, "meters");
        GroundSegment.push(segmentLength);

        if (segmentLength > maxSegmentLength) {
          maxSegmentLength = segmentLength;
        }
      }
    }
    let updatedGroundSegment = GroundSegment;
    return {
      length,
      updatedGroundSegment,
      maxSegmentLength,
    };
  }
  static finetunePolyline(paths) {
    if (paths[0].length > 2) {
      let lastNode = paths[0][paths[0].length - 1];
      let secondLastNode = paths[0][paths[0].length - 2];
      if (
        Math.abs(lastNode[0] - secondLastNode[0]) <= 1 &&
        Math.abs(lastNode[1] - secondLastNode[1]) <= 1
      ) {
        paths[0].splice(-1);
      }
    }
    return paths;
  }
}
export default geometryTools;
